import { RunFlow, type MissionLaunchHooks, type TestRangeCard } from "../flow/run-flow";
import { Engine } from "./engine";
import { loadSoldierAsset, type SoldierAsset } from "../ai/soldier-glb";
import type { LoadoutMods } from "../meta/loadout";
import type { MissionConfig } from "../mission/types";

// Boots the out-of-mission flow and owns the Engine lifecycle. All screen
// routing and meta/run state lives in flow/run-flow.ts; this class only
// starts and stops engines.

export class GameApp {
  private readonly flow: RunFlow;
  private activeEngine: Engine | null = null;
  // Preloaded at boot; missions start with animated GLB soldiers when it
  // resolves, or fall back to the legacy primitive rigs when it fails.
  private readonly soldierAssetPromise: Promise<SoldierAsset | undefined>;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    testRangeCards: TestRangeCard[],
  ) {
    this.soldierAssetPromise = loadSoldierAsset().catch((err) => {
      console.warn("[assets] soldier GLB failed to load — using primitive soldiers", err);
      return undefined;
    });

    this.flow = new RunFlow((config, loadout, hooks) => {
      void this.startMission(config, loadout, hooks);
    }, testRangeCards);
  }

  start(): void {
    this.flow.showMenu();
  }

  private async startMission(
    config: MissionConfig,
    loadout: LoadoutMods,
    hooks: MissionLaunchHooks,
  ): Promise<void> {
    const soldierAsset = await this.soldierAssetPromise; // resolved long before first mission start
    this.activeEngine?.stop();
    // Engine construction is deliberately synchronous (terrain sampling,
    // scatter, post buffers) and freezes the tab for a moment. Paint the
    // spool-up card BEFORE the freeze so it reads as loading, not a hang.
    const spool = this.showSpoolUp(config.name);
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    this.activeEngine = new Engine(this.canvas, loadout, config, {
      onMissionResult: hooks.onMissionResult,
      onReturnToMenu: () => {
        this.activeEngine?.stop();
        this.activeEngine = null;
        hooks.onReturnToMenu();
      },
    }, soldierAsset);
    spool.remove();
    this.activeEngine.start();
  }

  private showSpoolUp(missionName: string): HTMLElement {
    const el = document.createElement("div");
    el.className = "spool-up";
    el.innerHTML = `
      <div class="spool-up__title">SPOOLING UP</div>
      <div class="spool-up__mission">${missionName}</div>
      <div class="spool-up__hint">ENGINES · SENSORS · GUNS</div>
    `;
    document.body.appendChild(el);
    return el;
  }
}
